import { NavLink, useNavigate } from "react-router-dom";
import {
  House,
  MagnifyingGlass,
  FileText,
  SignOut,
  Buildings,
  Gear,
  ChartBar,
  ChartLine,
  CreditCard,
  Users,
  Bank,
  Question,
  Storefront,
  Bell,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import HelpChatWidget from "@/components/HelpChatWidget";
import Footer from "@/components/Footer";
import LifetimeBadge from "@/components/LifetimeBadge";

const userLinks = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/search", label: "Search Grants", icon: MagnifyingGlass, testid: "nav-search" },
  { to: "/saved-searches", label: "Alerts", icon: Bell, testid: "nav-saved-searches" },
  { to: "/proposals", label: "My Proposals", icon: FileText, testid: "nav-proposals" },
  { to: "/reporting", label: "Reporting", icon: ChartBar, testid: "nav-reporting" },
  { to: "/templates", label: "Templates", icon: Storefront, testid: "nav-templates" },
  { to: "/company", label: "Company", icon: Buildings, testid: "nav-company" },
  { to: "/subscribe", label: "Subscription", icon: CreditCard, testid: "nav-subscribe" },
  { to: "/help", label: "Help", icon: Question, testid: "nav-help" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

const adminLinks = [
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users" },
  { to: "/admin/bank-account", label: "Bank Account", icon: Bank, testid: "nav-admin-bank" },
  { to: "/admin/templates", label: "Templates", icon: Storefront, testid: "nav-admin-templates" },
  { to: "/admin/revenue", label: "Revenue", icon: ChartBar, testid: "nav-admin-revenue" },
  { to: "/admin/attribution", label: "Attribution", icon: ChartLine, testid: "nav-admin-attribution" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-zinc-50 text-zinc-900">
      <aside
        data-testid="sidebar"
        className="gf-sharp hidden md:flex w-64 flex-col border-r border-zinc-200 bg-white sticky top-0 h-screen"
      >
        <div className="px-6 py-6 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-700 flex items-center justify-center">
              <Buildings size={18} color="white" weight="bold" />
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight">
                GRANTFLOW
              </div>
              <div className="gf-overline mt-1">Federal Grants Suite</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {userLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm font-semibold border-l-4 ${
                  isActive
                    ? "border-blue-700 bg-zinc-100 text-zinc-900"
                    : "border-transparent text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <l.icon size={18} weight="regular" />
              <span className="uppercase tracking-wider text-xs">{l.label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <div className="px-6 mt-5 mb-2 gf-overline text-zinc-400">Admin</div>
              {adminLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-2.5 text-sm font-semibold border-l-4 ${
                      isActive
                        ? "border-blue-700 bg-zinc-100 text-zinc-900"
                        : "border-transparent text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                    }`
                  }
                >
                  <l.icon size={18} weight="regular" />
                  <span className="uppercase tracking-wider text-xs">{l.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="border-t border-zinc-200 p-4">
          <div className="mb-3">
            <div className="gf-overline">Signed in as</div>
            <div className="text-sm font-semibold mt-1 truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            {user?.subscription_status === "lifetime" ? (
              <div className="mt-2">
                <LifetimeBadge member={user} size="sm" />
              </div>
            ) : user?.subscription_status && (
              <div className="mt-2 inline-block text-[10px] px-2 py-0.5 bg-zinc-100 border border-zinc-200 font-mono uppercase tracking-wider text-zinc-700">
                {user.subscription_status}{user.is_premium ? ` · ${user.days_left}d` : ""}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            data-testid="logout-btn"
            onClick={onLogout}
            className="gf-sharp w-full border-zinc-300 uppercase font-bold tracking-wide"
          >
            <SignOut size={16} className="mr-2" /> Log out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Persistent top bar with Logout — visible on ALL screen sizes so users
            never have to hunt for it. On desktop the sidebar logout still exists;
            this is a redundant-but-obvious safety net. */}
        <div className="border-b border-zinc-200 bg-white flex items-center justify-between px-4 md:px-6 py-3 sticky top-0 z-30" data-testid="top-bar">
          <div className="font-display font-black tracking-tight md:hidden">GRANTFLOW</div>
          <div className="hidden md:block text-xs font-mono text-zinc-500 truncate">
            {user?.email}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onLogout}
            data-testid="top-logout-btn"
            className="gf-sharp uppercase font-bold tracking-wide"
          >
            <SignOut size={14} className="mr-1.5" /> Logout
          </Button>
        </div>
        <div className="px-6 md:px-8 lg:px-12 pt-4">
          <SubscriptionBanner />
        </div>
        <div className="p-6 md:p-8 lg:p-12 flex-1">{children}</div>
        <Footer />
      </main>
      <HelpChatWidget />
    </div>
  );
}
